// ============================================================
// OrientationAttendance — migrates /admin-panel/orientation-attendance
// Angular source: OrientationAttendanceComponent
//
// Features:
//   - Loads student list on mount via POST CGWillingStudList
//   - Shows table with P/A/N/A toggle buttons per student
//   - Checks if today's date is in allowed orientation dates list
//   - "Save" button opens confirmation dialog, then POSTs CGStudAttendanceUpdt
//
// PrimeReact: DataTable, Column, Dialog, Button, InputText, Toast
// Service: ../AdminPanel.service.ts (shared)
// ============================================================
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { RootState } from '../../../store'; // adjust path
import {
  useGetOrientationStudentListMutation,
  useSaveOrientationAttendanceMutation,
  OrientationStudent,
  AttendanceRecord,
} from '../AdminPanel.service';
import '../entrance-class/entrance-class.css'; // reuse same styles

// ── Allowed orientation dates (hardcoded as in Angular) ──────
const ORIENTATION_DATES: string[] = [
  '2024-10-26',
  '2024-11-16',
  '2024-11-17',
  '2024-11-18',
  '2024-11-19',
  '2024-11-20',
  '2024-11-23',
  '2024-11-24',
  '2024-11-25',
  '2024-11-26',
  '2024-11-27',
  '2024-12-02',
  '2024-12-03',
  '2024-12-04',
  '2024-12-07',
  '2024-12-08',
  '2024-12-09',
  '2024-12-10',
  '2024-12-11',
  '2024-12-14',
  '2024-12-15',
  '2024-12-16',
  '2024-12-17',
  '2024-12-18',
  '2024-12-21',
  '2024-12-22',
  '2024-12-23',
  '2024-12-24',
  '2024-12-25',
  '2025-01-04',
  '2025-01-05',
  '2025-01-06',
  '2025-01-07',
  '2025-01-08',
  '2025-01-11',
  '2025-01-12',
  '2025-01-13',
  '2025-01-14',
  '2025-01-15',
  '2025-01-18',
  '2025-01-19',
  '2025-01-20',
  '2025-01-21',
  '2025-01-22',
  '2025-01-25',
  '2025-01-26',
  '2025-01-27',
  '2025-01-28',
  '2025-01-29',
  '2025-02-01',
  '2025-02-02',
  '2025-02-03',
  '2025-02-04',
  '2025-02-05',
];

// ── Helpers ──────────────────────────────────────────────────
function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type AttenStatus = '1' | '2' | '3'; // 1=Present, 2=Absent, 3=N/A

interface StudentRow extends OrientationStudent {
  PrstAbst: AttenStatus;
}

// ── Component ────────────────────────────────────────────────

const OrientationAttendance: React.FC = () => {
  const toast = useRef<Toast>(null);
  const s = useSelector((state: RootState) => state.session);
  const teacherId = s.teacherId ?? '';

  const [studentList, setStudentList] = useState<StudentRow[]>([]);
  const [modifiedList, setModifiedList] = useState<AttendanceRecord[]>([]);
  const [studentShow, setStudentShow] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');

  const [fetchStudents, { isLoading }] = useGetOrientationStudentListMutation();
  const [saveAttendance, { isLoading: saving }] =
    useSaveOrientationAttendanceMutation();

  const today = useMemo(() => getTodayStr(), []);
  const isTodayAllowed = ORIENTATION_DATES.includes(today);

  // ── Load on mount ──
  async function loadData() {
    const res = await fetchStudents({
      TchrId: teacherId,
      Type: '1',
      Attdate: today,
    }).unwrap();
    if (res.dataStatus && res.result) {
      const rows: StudentRow[] = res.result.map((stu) => {
        let status: AttenStatus = '3'; // N/A by default
        if (isTodayAllowed) {
          status =
            stu.atten_status === '1' || stu.atten_status === '2'
              ? (stu.atten_status as AttenStatus)
              : '2'; // default Absent
        }
        return { ...stu, PrstAbst: status };
      });
      setStudentList(rows);
      setStudentShow(isTodayAllowed);
      setModifiedList([]);
    }
  }

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Toggle P/A per student ──
  function toggleAttendance(row: StudentRow) {
    const newStatus: AttenStatus = row.PrstAbst === '1' ? '2' : '1';
    // Update studentList
    setStudentList((prev) =>
      prev.map((s) =>
        s.student_id === row.student_id ? { ...s, PrstAbst: newStatus } : s
      )
    );
    // Track in modified list
    const record: AttendanceRecord = {
      id: row.id ?? '',
      student_id: row.student_id,
      atten_status: newStatus,
      atten_date: today,
    };
    setModifiedList((prev) => {
      const filtered = prev.filter((r) => r.student_id !== row.student_id);
      return [...filtered, record];
    });
  }

  // ── Save ──
  async function handleSave() {
    if (modifiedList.length === 0) {
      toast.current?.show({
        severity: 'warn',
        detail: 'No changes to save',
        life: 3000,
      });
      setConfirmDialog(false);
      return;
    }
    const res = await saveAttendance({ records: modifiedList }).unwrap();
    if (res.dataStatus) {
      toast.current?.show({
        severity: 'success',
        detail: res.message,
        life: 3000,
      });
      setConfirmDialog(false);
      loadData();
    } else {
      toast.current?.show({
        severity: 'error',
        detail: res.message,
        life: 3000,
      });
    }
  }

  // ── Status button color ──
  function getStatusClass(status: AttenStatus): string {
    if (status === '1') return 'solid-success-btn';
    if (status === '2') return 'solid-danger-btn';
    return 'solid-info-btn';
  }
  function getStatusLabel(status: AttenStatus): string {
    if (status === '1') return 'P';
    if (status === '2') return 'A';
    return 'N/A';
  }

  // ── Status column body ──
  const statusBody = (row: StudentRow) => (
    <button
      className={`py-2 px-3 border-0 ${getStatusClass(row.PrstAbst)}`}
      disabled={!studentShow}
      onClick={() => toggleAttendance(row)}
      style={{ minWidth: 40, fontWeight: 600, color: '#fff', borderRadius: 4 }}
    >
      {getStatusLabel(row.PrstAbst)}
    </button>
  );

  return (
    <div className="comparative-grid mt-3">
      <Toast ref={toast} />

      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6>
          <b>Career Guidance</b>
          <i className="pi pi-chevron-right mx-2" style={{ fontSize: 12 }} />
          <span>Attendance</span>
        </h6>
      </div>
      <div className="mb-2">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
          className="global-search-input"
        />
      </div>
      {/* Data Table */}
      <DataTable
        value={studentList}
        loading={isLoading}
        stripedRows
        showGridlines
        globalFilter={globalFilter}
        globalFilterFields={[
          'block_name',
          'udise_code',
          'school_name',
          'Studname',
          'StudEmisId',
          'Gender',
        ]}
        emptyMessage="No Records Found"
        // header={
        //   <div className="d-flex justify-content-end">
        //     <span className="p-input-icon-left">
        //       <InputText
        //         placeholder="Search..."
        //         value={globalFilter}
        //         onChange={(e) => setGlobalFilter(e.target.value)}
        //       />
        //     </span>
        //   </div>
        // }
      >
        <Column
          header="S.no"
          body={(_r, opts) => opts.rowIndex + 1}
          style={{ width: '4rem', textAlign: 'center' }}
        />
        <Column
          field="block_name"
          header="Block Name"
          style={{ textAlign: 'center' }}
        />
        <Column
          field="udise_code"
          header="Udise code"
          style={{ textAlign: 'center' }}
        />
        <Column
          field="school_name"
          header="School name"
          style={{ textAlign: 'center' }}
        />
        <Column
          field="StudEmisId"
          header="Student Id"
          style={{ textAlign: 'center' }}
        />
        <Column
          field="Studname"
          header="Student Name"
          style={{ textAlign: 'center' }}
        />
        <Column
          field="Gender"
          header="Gender"
          style={{ textAlign: 'center' }}
        />
        <Column
          header="Status"
          body={statusBody}
          style={{ textAlign: 'center' }}
        />
      </DataTable>

      {/* Save button */}
      {studentShow && (
        <div className="text-end m-4">
          <Button
            label="Save"
            className="btn btn-primary px-5 py-2"
            onClick={() => setConfirmDialog(true)}
          />
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        header="Attendance"
        visible={confirmDialog}
        modal
        closable={false}
        draggable={false}
        onHide={() => setConfirmDialog(false)}
        style={{ width: '30rem' }}
      >
        <div className="text-center py-3">
          <h4 className="mb-3">Are You sure?</h4>
          <h5>
            Do you submit attendance
            <br />
            You cannot resubmit it
          </h5>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              label="Cancel"
              className="solid-danger-btn px-5"
              onClick={() => setConfirmDialog(false)}
            />
            <Button
              label="Save"
              className="solid-success-btn px-5"
              loading={saving}
              onClick={handleSave}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default OrientationAttendance;
